import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp, CircleAlert, Clock3, Copy, Mail, MessageCircle, Plus, Search, Wallet } from "lucide-react";
import { track } from "../../lib/analytics";
import { initials } from "../../lib/format";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { photoBackground } from "../../components/Cards";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { ReservationModal } from "../../components/trips/ReservationModal";
import { formatCalendarDate, relativeTime } from "../../lib/format";
import { checkApartadoEligibility, useMyRequests, useReservationConfig, useTripPage } from "../../lib/queries";
import { GROUP_FILTERS, REQUEST_STATUS_LABELS, REQUEST_STATUS_TONES, amountWithCurrency, mxn, requestTimeline } from "../../lib/reservations";
import type { ReservationRequest, RequestStatusGroup } from "../../types";

const SHADE = "linear-gradient(180deg, rgba(0,37,87,0.1) 0%, rgba(0,37,87,0.75) 100%)";

function normalize(value: string) {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

/**
 * "Mis solicitudes de reservación" (A6, design 5a): every request of the account, searchable by
 * destination or folio, filtered by status group with counts; a persistent detail panel on desktop
 * and expandable cards on mobile. Filter and search live in the URL so opening and closing a
 * detail never loses them (A6.1).
 */
export function RequestsPage() {
  usePageHeader("Mis solicitudes de reservación", "Tu asesor responde en menos de 24 horas hábiles. Las propuestas tienen 7 días de vigencia.");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requests = useMyRequests();
  const filter = (searchParams.get("filtro") as RequestStatusGroup | "all" | null) ?? "all";
  const query = searchParams.get("q") ?? "";
  const selectedId = searchParams.get("sel");
  const [expanded, setExpanded] = useState<string | null>(selectedId);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const list = requests.data?.requests ?? [];
  const visible = useMemo(() => {
    const needle = normalize(query);
    return list.filter((request) => (filter === "all" || request.statusGroup === filter)
      && (!needle || normalize(request.trip.name).includes(needle) || normalize(request.folio).includes(needle) || normalize(request.trip.summary ?? "").includes(needle)));
  }, [list, filter, query]);
  const selected = visible.find((request) => request.id === selectedId) ?? visible[0] ?? null;
  useEffect(() => { if (selected && selected.id !== selectedId) setParam("sel", selected.id); }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Analytics: the view with its filter, each search (debounced) and each request opened (A6).
  useEffect(() => { if (requests.data) track("reservation_history_viewed", { active_filter: filter, result_count: visible.length }); }, [requests.data, filter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!query.trim()) return;
    const timer = window.setTimeout(() => track("reservation_history_searched", { query_type: /^av-?\d+$/i.test(query.trim()) ? "folio" : "destination", result_count: visible.length }), 700);
    return () => window.clearTimeout(timer);
  }, [query, visible.length]);
  useEffect(() => { if (selected) track("reservation_history_request_opened", { request_id: selected.id, status: selected.status }); }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (requests.isLoading) return <div className="page-state"><Spinner label="Cargando solicitudes" /><span>Cargando solicitudes</span></div>;
  if (requests.isError) {
    return <div className="page-state error"><CircleAlert aria-hidden="true" /><strong>No pudimos cargar tus solicitudes</strong><span>Tu navegación sigue disponible; vuelve a intentarlo en un momento.</span><button className="secondary-button" onClick={() => void requests.refetch()}>Reintentar</button></div>;
  }
  const counts = requests.data?.counts ?? { all: 0, review: 0, proposal: 0, confirmed: 0, closed: 0 };

  return (
    <div className="page-container requests-page">
      <button type="button" className="back-button" onClick={() => navigate("/wallet/travel")}><ArrowLeft size={16} aria-hidden="true" /> Apartados</button>
      <div className="discover-toolbar">
        <label className="search-field">
          <Search size={18} aria-hidden="true" />
          <input type="search" value={query} onChange={(event) => setParam("q", event.target.value || null)} placeholder="Buscar por destino o folio" aria-label="Buscar por destino o folio" />
        </label>
        <button type="button" className="primary-button solid" onClick={() => navigate("/explorar")}><Plus size={16} aria-hidden="true" /> Nueva solicitud</button>
      </div>
      <div className="chip-row" role="group" aria-label="Filtrar por estado">
        {GROUP_FILTERS.map((option) => (
          <button key={option.id} type="button" className={`chip${filter === option.id ? " active" : ""}`} aria-pressed={filter === option.id} onClick={() => setParam("filtro", option.id === "all" ? null : option.id)}>
            {option.label} <span className="chip-count" translate="no">{counts[option.id]}</span>
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="empty-state">
          <Clock3 size={28} aria-hidden="true" />
          <strong>Aún no tienes solicitudes</strong>
          <span>Explora los viajes y solicita una reservación o un viaje a la medida.</span>
          <button type="button" className="secondary-button" onClick={() => navigate("/explorar")}>Explorar viajes</button>
        </div>
      ) : (
        <div className="requests-grid">
          <div className="requests-list" role="list">
            {visible.length === 0 && (
              <div className="empty-state">
                <strong>{filter === "all" && !query ? "Nada por aquí" : "Sin solicitudes en este estado"}</strong>
                <span>Cambia el filtro o empieza una nueva solicitud.</span>
                <div className="inline-actions">
                  <button type="button" className="secondary-button small" onClick={() => { setParam("filtro", null); setParam("q", null); }}>Quitar filtros</button>
                  <button type="button" className="link-button" onClick={() => navigate("/explorar")}>Nueva solicitud</button>
                </div>
              </div>
            )}
            {visible.map((request) => {
              const isSelected = selected?.id === request.id;
              const isExpanded = expanded === request.id;
              return (
                <article key={request.id} className={`request-card${isSelected ? " selected" : ""}`} role="listitem">
                  <button type="button" className="request-summary" onClick={() => { setParam("sel", request.id); setExpanded(isExpanded ? null : request.id); }} aria-expanded={isExpanded}>
                    <span className="request-photo" style={photoBackground(request.trip.photoUrl, SHADE, "thumb")} aria-hidden="true" />
                    <span className="request-copy">
                      <span className="request-top">
                        <span className={`status-pill ${REQUEST_STATUS_TONES[request.status]}`}>{REQUEST_STATUS_LABELS[request.status]}</span>
                        {request.kind === "personalized" && <span className="tag">Viaje a la medida</span>}
                        <span className="request-meta" translate="no">#{request.folio} · {relativeTime(request.lastActivityAt)}</span>
                      </span>
                      <strong>{request.trip.name}</strong>
                      <span className="request-meta">{summaryLine(request)}</span>
                    </span>
                    <span className="request-price">
                      <small>{priceLabel(request)}</small>
                      <strong translate="no">{priceValue(request)}</strong>
                      <span className="request-chevron">{isExpanded ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}</span>
                    </span>
                  </button>
                  {isExpanded && <div className="request-mobile-detail"><RequestDetail request={request} compact /></div>}
                </article>
              );
            })}
          </div>
          <aside className="requests-detail">
            {selected ? <RequestDetail request={selected} /> : <div className="empty-state"><strong>Selecciona una solicitud</strong><span>Verás su seguimiento y las acciones disponibles.</span></div>}
          </aside>
        </div>
      )}
    </div>
  );
}

function summaryLine(request: ReservationRequest): string {
  const parts = [
    request.departureDate ? formatCalendarDate(request.departureDate) : request.kind === "personalized" ? "Fechas por definir" : null,
    `${request.travelers} ${request.travelers === 1 ? "viajero" : "viajeros"}`,
    request.occupancyLabel
  ].filter(Boolean);
  return parts.join(" · ");
}

function priceLabel(request: ReservationRequest): string {
  if (request.status === "confirmed" || request.status === "services_confirmed") return "Total";
  if (request.pricing.quotedTotal !== null) return "Cotizado";
  return request.kind === "personalized" ? "Presupuesto" : "Estimado";
}

function priceValue(request: ReservationRequest): string {
  if (request.pricing.quotedTotal !== null) return amountWithCurrency(request.pricing.quotedTotal, request.pricing.quotedCurrency ?? request.pricing.currency);
  if (request.pricing.estimatedTotal !== null) return amountWithCurrency(request.pricing.estimatedTotal, request.pricing.currency);
  return "Por cotizar";
}

/** The selected request: trip summary, amounts, proposal validity, timeline and next actions (A6.2 / A6.3). */
export function RequestDetail({ request, compact = false }: { request: ReservationRequest; compact?: boolean }) {
  const navigate = useNavigate();
  const config = useReservationConfig();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const trip = useTripPage(duplicateOpen && request.trip.slug ? request.trip.slug : undefined);
  const timeline = requestTimeline(request);
  const closed = request.statusGroup === "closed";
  // A personalized proposal becomes apartable once the advisor has quoted it (A6.3).
  const canCreateApartado = !closed && !request.apartado && (request.kind === "reservation" || request.pricing.quotedTotal !== null);

  const startApartado = async () => {
    setBusy(true); setNote(null);
    try {
      // Revalidate status, proposal validity and duplicates before Flow C opens (FR24).
      const eligibility = await checkApartadoEligibility(request.id);
      track("apartado_started_from_history", { request_id: request.id, trip_id: request.trip.id, eligibility_state: eligibility.eligible ? "eligible" : eligibility.existingApartadoId ? "existing" : "ineligible" });
      if (eligibility.eligible) navigate(`/solicitudes/${request.id}/apartado`);
      else if (eligibility.existingApartadoId) navigate(`/apartados/${eligibility.existingApartadoId}`);
      else setNote(eligibility.message);
    } catch { setNote("No pudimos validar la solicitud. Intenta de nuevo."); }
    finally { setBusy(false); }
  };

  const facts: Array<[string, string]> = [
    ["Salida", request.departureDate ? formatCalendarDate(request.departureDate, "long") : "Por definir"],
    ["Viajeros", `${request.travelers} (${request.adults} ${request.adults === 1 ? "adulto" : "adultos"}${request.children ? ` · ${request.children} ${request.children === 1 ? "niño" : "niños"}` : ""})`],
    ...(request.occupancyLabel ? [["Habitaciones", request.occupancyLabel] as [string, string]] : []),
    ...(request.childAges.length ? [["Edades de niños", request.childAges.map((age) => `${age} años`).join(", ")] as [string, string]] : []),
    ...(request.kind === "reservation" ? [["Pasaportes", request.passportConfirmed ? "Vigentes ✓" : "Por confirmar"] as [string, string]] : []),
    ...(request.kind === "personalized" ? personalizedFacts(request) : [])
  ];

  return (
    <div className={`request-detail${compact ? " compact" : ""}`}>
      {!compact && (
        <div className="request-detail-hero" style={photoBackground(request.trip.photoUrl, SHADE, "wide")}>
          <span className={`status-pill ${REQUEST_STATUS_TONES[request.status]}`}>{REQUEST_STATUS_LABELS[request.status]}</span>
          <h3>{request.trip.name}</h3>
          <span translate="no">{request.trip.summary ? `${request.trip.summary} · ` : ""}#{request.folio}</span>
        </div>
      )}
      <div className="request-amount">
        <div><small>{priceLabel(request)}</small><strong translate="no">{priceValue(request)}</strong></div>
        <span className="request-meta">
          {request.pricing.quotedTotal !== null && request.pricing.proposalValidUntil
            ? `Propuesta vigente hasta el ${formatCalendarDate(request.pricing.proposalValidUntil, "long")}`
            : request.pricing.pricePerPerson !== null ? `${amountWithCurrency(request.pricing.pricePerPerson, request.pricing.currency)} por persona · aún sin confirmar` : "Tu asesor te enviará la cotización"}
        </span>
        {request.pricing.estimatedTotalMxn !== null && <span className="request-meta" translate="no">≈ {mxn(request.pricing.quotedTotal !== null && request.pricing.quotedCurrency === "MXN" ? request.pricing.quotedTotal : request.pricing.estimatedTotalMxn)} al tipo de cambio de referencia</span>}
      </div>

      <span className="rail-title section-eyebrow">Seguimiento</span>
      <ol className="timeline">
        {timeline.map((step) => (
          <li key={step.key} className={step.state}><span className="timeline-dot" aria-hidden="true" /><div><strong>{step.title}</strong><span>{step.detail}</span></div></li>
        ))}
      </ol>
      {request.advisorNote && <p className="advisor-note"><strong>Tu asesor:</strong> {request.advisorNote}</p>}
      {config.data && <AdvisorContact advisor={config.data.advisor} request={request} />}

      <span className="rail-title section-eyebrow">Lo que solicitaste</span>
      <dl className="kv-list">{facts.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>

      {request.apartado && (
        <div className="request-apartado">
          <Wallet size={16} aria-hidden="true" />
          {request.apartado.status === "closed"
            ? <div><strong>Apartado cerrado</strong><span>La solicitud se cerró y el dinero apartado regresó a tu Wallet Libre. Puedes verlo en el historial del Apartado.</span></div>
            : <div><strong>Apartado para este viaje</strong><span translate="no">{mxn(request.apartado.balance)} apartados de una meta de {mxn(request.apartado.goalAmount)}</span></div>}
        </div>
      )}
      {note && <div className="form-message" role="status">{note}</div>}
      <div className="request-actions">
        {request.trip.slug && <button type="button" className="secondary-button" onClick={() => navigate(`/viajes/${request.trip.slug}`)}>Ver viaje</button>}
        {request.apartado
          ? <button type="button" className="primary-button solid" onClick={() => navigate(`/apartados/${request.apartado!.id}`)}>Ver apartado <ArrowRight size={15} aria-hidden="true" /></button>
          : canCreateApartado && <button type="button" className="primary-button solid" onClick={() => void startApartado()} disabled={busy}>{busy ? "Validando…" : "Crear apartado"}</button>}
        {request.kind === "reservation" && request.trip.slug && (
          <button type="button" className={closed ? "primary-button" : "link-button"} onClick={() => setDuplicateOpen(true)}><Copy size={14} aria-hidden="true" /> Duplicar solicitud con otras fechas</button>
        )}
        {request.kind === "personalized" && !closed && <button type="button" className="link-button" onClick={() => navigate("/viajes/a-la-medida")}>Solicitar otro viaje</button>}
      </div>
      {config.data && <p className="resv-fine">Las propuestas tienen {config.data.proposalValidityDays} días de vigencia. Crear o abonar un Apartado no confirma la reservación.</p>}
      {duplicateOpen && trip.data && (
        <ReservationModal
          trip={trip.data} open={duplicateOpen} onOpenChange={setDuplicateOpen} duplicatedFrom={request.id}
          prefill={{ adults: request.adults, children: request.children, childAges: request.childAges, rooms: request.rooms, departureDate: null }}
          onSubmitted={(created) => { setDuplicateOpen(false); navigate(`/solicitudes/${created.id}`, { state: { justSubmitted: true } }); }}
        />
      )}
      {duplicateOpen && trip.isError && <div className="form-error" role="alert">Este viaje ya no está publicado; escríbenos para duplicar tu solicitud.</div>}
    </div>
  );
}

/**
 * The advisor card of design 5a: who follows the request and "Escribir" — WhatsApp when the team
 * configured a number, mail otherwise. The customer's own contact preference is echoed back.
 */
function AdvisorContact({ advisor, request }: { advisor: { name: string; email: string; whatsapp: string }; request: ReservationRequest }) {
  const preference = String((request.details as Record<string, unknown>).contactPreference ?? "");
  const preferenceLabel = preference === "whatsapp" ? "WhatsApp" : preference === "email" ? "correo" : preference === "call" ? "llamada" : null;
  const text = encodeURIComponent(`Hola, escribo por mi solicitud ${request.folio} (${request.trip.name}).`);
  const whatsapp = advisor.whatsapp.replace(/\D/g, "");
  const href = whatsapp ? `https://wa.me/${whatsapp}?text=${text}` : advisor.email ? `mailto:${advisor.email}?subject=${encodeURIComponent(`Solicitud ${request.folio} · ${request.trip.name}`)}&body=${text}` : null;
  const closed = request.statusGroup === "closed";
  return (
    <div className="advisor-contact">
      <span className="avatar" aria-hidden="true">{initials(advisor.name)}</span>
      <div className="advisor-contact-copy">
        <strong>{advisor.name || "Asesor de viajes Aventuria"}</strong>
        <span>{closed ? "Atendió esta solicitud" : preferenceLabel ? `Te contacta por ${preferenceLabel} · Asesor de viajes Aventuria` : "Asesor de viajes Aventuria"}</span>
      </div>
      {href
        ? <a className="secondary-button small" href={href} target={whatsapp ? "_blank" : undefined} rel={whatsapp ? "noreferrer" : undefined}>{whatsapp ? <MessageCircle size={14} aria-hidden="true" /> : <Mail size={14} aria-hidden="true" />} Escribir</a>
        : <span className="request-meta">Tu asesor te contactará</span>}
    </div>
  );
}

function personalizedFacts(request: ReservationRequest): Array<[string, string]> {
  const details = request.details as Record<string, unknown>;
  const style: Record<string, string> = { own: "Por mi cuenta", group: "En grupo", private_guide: "Con guía privado", unsure: "No estoy seguro" };
  const facts: Array<[string, string]> = [];
  if (typeof details.origin === "string") facts.push(["Origen", details.origin]);
  if (details.datesMode === "flexible") facts.push(["Fechas", [details.flexiblePeriod, details.durationText].filter(Boolean).join(" · ") || "Flexibles"]);
  if (typeof details.style === "string") facts.push(["Estilo", style[details.style] ?? String(details.style)]);
  if (details.budgetMode === "guidance") facts.push(["Presupuesto", "Quiere orientación"]);
  return facts;
}
